import { FRAMES, HUD } from "odyssey-shared";
import { KIT_METRICS } from "../../ui/kit/metrics.ts";
import { pageRoom, type PlaceRoom } from "../../ui/kit/place.ts";
import { BAG_WINDOW } from "../bag/layout.ts";
import { slotRect } from "../hud/area-slots.ts";
import { HUD_SLOTS, resolveSlot, type HudArea, type SlotRect } from "../hud/slots.ts";

/**
 * Where the loot window stands and how large it is (P2.8-T16, ADR-091 decision 22; the design's `loot` card in
 * `ItemSurfaces.jsx`, a visual reference: "Window 150 wide at 380,112, beside the chest, clear of the centre";
 * "Rows: slot 20, name, Take button; gap 2"), with the row's slot the bag's 24 so an item's colour art is drawn the same
 * size in both (ADR-111 decision 15 and its ruling 8). Pure: the window lays itself out from these.
 */

/**
 * The window's geometry in logical pixels: the design's 150 wide; its left edge `fromCentre` right of the area's
 * centre and its top `aboveCentre` above it, which is the design's 380,112 on the 640 by 360 grid (where the toast
 * column moves it left to 356, `chestWindowBox`) and beside the character, who stands at the centre with the chest, on
 * any other; rows of a 24 px slot, the bag's, `rowGap` apart, a row's parts `nameGap`
 * apart; the empty state `emptyHeight` tall, which holds its glyph and two lines of its sentence; `headerRule`, the
 * line the kit draws under a window's header; `minHeight`, the
 * least a window shortened by a small area is drawn, one row's window, its content scrolling down; and `clearTop`, the highest it ever
 * rises, which is the Inventory window's, under the band where the staff Feedback button stands over the page; and
 * `nearCentre`, the nearest its left edge comes to the centre when it moves left to clear the toast column, room for the
 * character and the chest beside it.
 */
export const CHEST_WINDOW = { width: 150, fromCentre: 60, aboveCentre: 68, slot: 24, rowGap: 2, nameGap: 4, emptyHeight: 68, headerRule: 1, minHeight: 103, clearTop: BAG_WINDOW.clearTop, nearCentre: 32 } as const;

/**
 * The window's height for a number of rows: the window frame around its header, its padded body and, with a row to
 * take, the footer that holds Take all; with no row, the empty state in the rows' place and no footer.
 *
 * @param rows How many rows the window draws.
 * @returns The height in logical pixels.
 * @throws RangeError when `rows` is not a whole number from 0.
 * @example
 * chestWindowHeight(4);
 * // 181
 */
export function chestWindowHeight(rows: number): number {
  if (!Number.isInteger(rows) || rows < 0) {
    throw new RangeError(`${rows} is not a number of rows`);
  }
  const frame = FRAMES.window.insets;
  const metrics = KIT_METRICS.window;
  const chrome = frame.top + metrics.header + CHEST_WINDOW.headerRule + 2 * metrics.padding + frame.bottom;
  if (rows === 0) {
    return chrome + CHEST_WINDOW.emptyHeight;
  }
  const footer = metrics.footer.top + KIT_METRICS.button.height + metrics.footer.bottom;
  return chrome + rows * CHEST_WINDOW.slot + (rows - 1) * CHEST_WINDOW.rowGap + footer;
}

/**
 * The window's box on an area: the design's width, `fromCentre` right of the centre and `aboveCentre` above it, as
 * tall as its rows need; on an area too small for that it is held inside the area's margins, moved up as far as
 * `clearTop` and then shortened so it stays whole above the XP bar over the ability bar, never under `minHeight`
 * unless its rows need less. Where it would come within a gutter of the toast column, as on the 640 by 360 grid, it
 * moves left to a gutter short of the column, when that keeps it `nearCentre` or more right of the centre; on an area
 * too narrow for that (under 632 wide) it keeps its place and shares the column's.
 *
 * @param area The HUD's area, whole logical pixels.
 * @param rows How many rows the window draws.
 * @returns The box from the area's top left, whole logical pixels.
 * @throws RangeError when `rows` is not a whole number from 0.
 * @example
 * chestWindowBox({ width: 640, height: 360 }, 4);
 * // { left: 356, top: 112, width: 150, height: 181 }
 */
export function chestWindowBox(area: HudArea, rows: number): SlotRect {
  const wanted = chestWindowHeight(rows);
  const width = Math.min(CHEST_WINDOW.width, area.width - 2 * HUD.margin);
  const left = Math.max(HUD.margin, Math.min(Math.floor(area.width / 2) + CHEST_WINDOW.fromCentre, area.width - HUD.margin - width));
  const floor = resolveSlot(HUD_SLOTS.xp, area).top - HUD.gutter;
  const top = Math.max(CHEST_WINDOW.clearTop, Math.min(Math.floor(area.height / 2) - CHEST_WINDOW.aboveCentre, floor - wanted));
  const height = Math.max(Math.min(wanted, CHEST_WINDOW.minHeight), Math.min(wanted, floor - top));
  const toasts = slotRect("toasts", area);
  const near = left + width + HUD.gutter > toasts.left && toasts.left + toasts.width + HUD.gutter > left && top + height + HUD.gutter > toasts.top && toasts.top + toasts.height + HUD.gutter > top;
  const clear = toasts.left - HUD.gutter - width;
  return { left: near && clear - Math.floor(area.width / 2) >= CHEST_WINDOW.nearCentre ? Math.min(left, clear) : left, top, width, height };
}

/**
 * The room the window is held inside once it has been moved (ADR-061's amendment "a window can be moved"): the area
 * less the HUD's margin, starting at `clearTop`, as the Inventory window's is.
 *
 * @param area The HUD's area.
 * @returns The room, whole logical pixels.
 * @example
 * chestRoom({ width: 640, height: 360 });
 * // { left: 4, top: 32, right: 636, bottom: 356 }
 */
export function chestRoom(area: HudArea): PlaceRoom {
  return pageRoom(area, HUD.margin, CHEST_WINDOW.clearTop);
}
